<template>
    <div class="modal fade show" aria-labelledby="addNewCardTitle" style="display: block; background: #1c1d1c33; z-index: 9999;">
        <div class="modal-dialog modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h1 class="mb-0">{{ (isEdit ? 'Edit' : 'Add') + ' API Provider' }}</h1>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="pb-2">
                    <div class="col-11 py-1 mx-auto">
                        
                        <div class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Endpoint (Domain/URL)</label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="formData.endpoint"
                                placeholder="https://example.com"
                            />
                            <transition name="fade">
                                <span v-if="formError.endpoint" class="invalid-feedback d-block qcont">{{ formError.endpoint }}</span>
                            </transition>
                        </div>

                        <div class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Authorized Services</label>
                            <v-select
                                multiple
                                placeholder="Select Services"
                                :options="servicesList"
                                v-model="formData.services"
                                :reduce="service => service.value"
                                label="label"
                            />
                            <transition name="fade">
                                <span v-if="formError.services" class="invalid-feedback d-block qcont">{{ formError.services }}</span>
                            </transition>
                        </div>

                        <div class="mb-1" v-if="formData.services && formData.services.includes('receipt')">
                            <label class="form-label"> <span class="required">*</span> Received In</label>
                            <v-select
                                v-model="formData.account_head_id"
                                placeholder="Select Cash & Bank Account"
                                class="w-100"
                                :options="accountHeads"
                                label="name"
                                :reduce="(name) => name.id"
                            />
                            <transition name="fade">
                                <span v-if="formError.account_head_id" class="invalid-feedback d-block qcont">{{ formError.account_head_id }}</span>
                            </transition>
                        </div>

                        <div class="mb-1">
                            <label class="form-label"> Contact Profile</label>
                            <AsyncSelect
                                placeholder="Select Contact Profile"
                                :api-service="fetchContactProfileDropdownList"
                                v-model="formData.contact_profile_id"
                                :additional-query="{ company_id: route.params.companyId }"
                                label="name"
                            />
                        </div>

                        <div class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Expires At</label>
                            <input 
                                type="date" 
                                class="form-control" 
                                v-model="formData.expires_at"
                                :min="minDate"
                            />
                            <transition name="fade">
                                <span v-if="formError.expires_at" class="invalid-feedback d-block qcont">{{ formError.expires_at }}</span>
                            </transition>
                        </div>

                        <div v-if="isEdit" class="mb-1">
                            <label class="form-label"> <span class="required">*</span> Status</label>
                            <v-select
                                placeholder="Select Status"
                                v-model="formData.status"
                                :options="statusOptions"
                                :reduce="option => option.value"
                                label="label"
                            />
                        </div>

                        <div class="d-flex justify-content-end mt-3">
                            <button @click="onClickClose" type="button" class="btn btn-outline-secondary me-1">Cancel</button>
                            <button @click="onSubmit" type="button" class="btn btn-primary" :disabled="loading">
                                <span v-if="loading" class="spinner-border spinner-border-sm me-50" role="status" aria-hidden="true"></span>
                                {{ isEdit ? 'Update' : 'Save' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { reactive, ref, inject } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import handleApiIntegrationHub from '@/services/modules/apiIntegrationHub';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';

const props = defineProps({
    isEdit: Boolean,
    formData: Object,
    editData: Object,
    servicesList: Array,
    accountHeads: Array,
});

const emit = defineEmits(['onSaved']);

const store = useStore();
const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);

const { storeApiProvider, updateApiProvider } = handleApiIntegrationHub();
import handleDropdowns from '@/services/modules/dropdowns';
const { fetchContactProfileDropdownList } = handleDropdowns();



const statusOptions = ref([
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
]);

const formError = reactive({
    endpoint: '',
    services: '',
    expires_at: '',
    account_head_id: ''
});

const minDate = new Date().toISOString().split('T')[0];

const checkValidation = () => {
    let flag = false;
    formError.endpoint = '';
    formError.services = '';
    formError.account_head_id = '';

    if (!props.formData.endpoint) {
        formError.endpoint = 'Endpoint is required';
        flag = true;
    }
    
    if (!props.formData.services || props.formData.services.length === 0) {
        formError.services = 'Select at least one service';
        flag = true;
    }
    
    if (props.formData.services && props.formData.services.includes('receipt') && !props.formData.account_head_id) {
        formError.account_head_id = 'Received In is required for receipt service';
        flag = true;
    }

    if (!props.formData.expires_at) {
        formError.expires_at = 'Expires at is required';
        flag = true;
    }
    
    if (props.formData.expires_at && props.formData.expires_at < minDate) {
        formError.expires_at = 'Expires at cannot be less than today';
        flag = true;
    }

    return flag;
}

const getQuery = () => {
    return `?company_id=${route.params.companyId}`;
}

const handleUpdate = async (query) => {
    const res = await updateApiProvider(props.formData.id, {
        status: props.formData.status,
        services: props.formData.services,
        endpoint: props.formData.endpoint ? props.formData.endpoint.trim() : '',
        contact_profile_id: props.formData.contact_profile_id && typeof props.formData.contact_profile_id === 'object' ? props.formData.contact_profile_id.id : props.formData.contact_profile_id,
        account_head_id: props.formData.services && props.formData.services.includes('receipt') ? props.formData.account_head_id : null,
        expires_at: props.formData.expires_at,
        type: 'local'
    }, query);

    if (!res.status) {
        showError(res.message || 'Failed to update');
        return false;
    }

    showSuccess(res.message || 'Updated successfully');
    emit('onSaved');
    return true;
}

const handleStore = async (query) => {
    const payload = {
        endpoint: props.formData.endpoint,
        services: props.formData.services,
        expires_at: props.formData.expires_at,
        contact_profile_id: props.formData.contact_profile_id && typeof props.formData.contact_profile_id === 'object' ? props.formData.contact_profile_id.id : props.formData.contact_profile_id,
        account_head_id: props.formData.services && props.formData.services.includes('receipt') ? props.formData.account_head_id : null,
        type: 'local'
    };
    const res = await storeApiProvider(payload, query);

    if (!res.status) {
        showError(res.message || 'Failed to save');
        return false;
    }

    showSuccess(res.message || 'Saved successfully');
    if (res.api_key) {
        emit('onSaved', res.api_key);
    }
    if (!res.api_key) {
        emit('onSaved');
    }
    return true;
}

const onSubmit = async () => {
    if (checkValidation()) return;
    loading.value = true;

    try {
        const query = getQuery();
        let success = false;

        if (props.isEdit) {
            success = await handleUpdate(query);
        }
        
        if (!props.isEdit) {
            success = await handleStore(query);
        }

        if (success) {
            onClickClose();
        }
    } catch (err) {
        console.error(err);
        showError('An error occurred while saving.');
    } finally {
        loading.value = false;
    }
}

const onClickClose = () => {
    store.state.isModalOpenTwo = false;
}
</script>

<style scoped>
.required {
    color: red;
}
</style>
